import _ from "lodash";
import { call, put, select, takeLatest } from "redux-saga/effects";
import { editCompanyBank, editCompanyPad, setLoading } from "../actions";
import { Auth } from "../utilities/auth";
import axios from "../utilities/axiosWrapper";
import { ReadResponse } from "./utilities/helper";

// Auth reducer
export const selectAuthReducer = (state: any) => state.authReducer;

/**
 * @param {*} body
 * @return {string} the API's message, or a generic fallback
 */
const readErrorMessage = (body: any): string =>
	body?.error?.message ?? "Something went wrong. Please try again.";

/**
 * @desc Fetch a company's pre-authorized debit banking details from the super
 * admin endpoint. This is a company scoped route, distinct from the person
 * banking route used by the plan member panel.
 * @param {*} action
 * @return {*}  {Generator}
 */
export function* fetchCompanyBankWorker(action: any): Generator {
	yield put(setLoading({ loading: true, errorMessage: "" }));
	let selectedAuthReducer: any = yield select(selectAuthReducer);
	let apikeys: any = null;
	let jsonResponse: any = null;
	let params: any = {
		method: "get",
		path: `businesses/${action.data}/bank?at=${selectedAuthReducer.accessToken}`,
		data: {},
	};

	try {
		apikeys = yield Auth.getAuthTokens();
		jsonResponse = yield call(axios, params, apikeys);

		const { status, body } = ReadResponse(jsonResponse);

		// A company with no banking details on file is an expected state, not an
		// error, so the panel opens empty and ready to be filled in.
		if (status === 404) {
			yield put(editCompanyBank({}));
			return yield put(setLoading({ loading: false }));
		}

		if (status >= 400) {
			yield put(editCompanyBank({}));
			return yield put(
				setLoading({
					loading: false,
					alertType: "error",
					errorMessage: readErrorMessage(body),
				})
			);
		}

		yield put(editCompanyBank(_.get(body, "data", {})));
		return yield put(setLoading({ loading: false }));
	} catch (ex) {
		yield put(editCompanyBank({}));
		return yield put(setLoading({ loading: false }));
	}
}

/**
 * @desc Save a company's pre-authorized debit banking details.
 * @param {*} action
 * @return {*}  {Generator}
 */
export function* updateCompanyBankWorker(action: any): Generator {
	yield put(
		setLoading({ loading: true, errorMessage: "", successMessage: "" })
	);
	let apikeys: any = null;
	let jsonResponse: any = null;

	try {
		apikeys = yield Auth.getAuthTokens();
		jsonResponse = yield call(axios, action.data, apikeys);

		const { status, body } = ReadResponse(jsonResponse);

		if (status >= 200 && status < 300) {
			return yield put(
				setLoading({
					loading: false,
					alertType: "success",
					successMessage: "Company banking details successfully updated",
				})
			);
		}

		return yield put(
			setLoading({
				loading: false,
				alertType: "error",
				errorMessage: readErrorMessage(body),
			})
		);
	} catch (ex) {
		return yield put(
			setLoading({ loading: false, errorMessage: ex.message })
		);
	}
}

/**
 * @desc Fetch whether a company has signed up for pre-authorized debit.
 * @param {*} action
 * @return {*}  {Generator}
 */
export function* fetchCompanyPadWorker(action: any): Generator {
	let selectedAuthReducer: any = yield select(selectAuthReducer);
	let apikeys: any = null;
	let jsonResponse: any = null;
	let params: any = {
		method: "get",
		path: `businesses/${action.data}/pad?at=${selectedAuthReducer.accessToken}`,
		data: {},
	};

	try {
		apikeys = yield Auth.getAuthTokens();
		jsonResponse = yield call(axios, params, apikeys);

		const { status, body } = ReadResponse(jsonResponse);

		if (status >= 400) {
			return yield put(
				setLoading({
					loading: false,
					alertType: "error",
					errorMessage: readErrorMessage(body),
				})
			);
		}

		return yield put(editCompanyPad(_.get(body, "data", {})));
	} catch (ex) {
		return yield put(
			setLoading({
				loading: false,
				alertType: "error",
				errorMessage: "Could not read the pre-authorized debit status.",
			})
		);
	}
}

/**
 * @desc Turn a company's pre-authorized debit sign-up on or off.
 * @param {*} action
 * @return {*}  {Generator}
 */
export function* updateCompanyPadWorker(action: any): Generator {
	yield put(
		setLoading({ loading: true, errorMessage: "", successMessage: "" })
	);
	let selectedAuthReducer: any = yield select(selectAuthReducer);
	let apikeys: any = null;
	let jsonResponse: any = null;
	let params: any = {
		method: "post",
		path: `businesses/${action.data.businessGuid}/pad?at=${selectedAuthReducer.accessToken}`,
		data: { signedUp: action.data.signedUp },
	};

	try {
		apikeys = yield Auth.getAuthTokens();
		jsonResponse = yield call(axios, params, apikeys);

		const { status, body } = ReadResponse(jsonResponse);

		if (status >= 200 && status < 300) {
			yield put(editCompanyPad(_.get(body, "data", {})));
			return yield put(
				setLoading({
					loading: false,
					alertType: "success",
					successMessage: action.data.signedUp
						? "Company signed up for pre-authorized debit"
						: "Pre-authorized debit sign-up removed",
				})
			);
		}

		return yield put(
			setLoading({
				loading: false,
				alertType: "error",
				errorMessage: readErrorMessage(body),
			})
		);
	} catch (ex) {
		return yield put(
			setLoading({ loading: false, errorMessage: ex.message })
		);
	}
}

/**
 * @return {*}  {Generator}
 */
function* fetchCompanyPadWatcher(): Generator {
	yield takeLatest("FETCH_COMPANY_PAD", fetchCompanyPadWorker);
}

/**
 * @return {*}  {Generator}
 */
function* updateCompanyPadWatcher(): Generator {
	yield takeLatest("UPDATE_COMPANY_PAD", updateCompanyPadWorker);
}

/**
 * @return {*}  {Generator}
 */
function* fetchCompanyBankWatcher(): Generator {
	yield takeLatest("FETCH_COMPANY_BANK", fetchCompanyBankWorker);
}

/**
 * @return {*}  {Generator}
 */
function* updateCompanyBankWatcher(): Generator {
	yield takeLatest("UPDATE_COMPANY_BANK", updateCompanyBankWorker);
}

export {
	fetchCompanyBankWatcher,
	updateCompanyBankWatcher,
	fetchCompanyPadWatcher,
	updateCompanyPadWatcher,
};
